<script setup>
import { computed, onBeforeUnmount, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import addDays from 'date-fns/addDays';
import differenceInCalendarDays from 'date-fns/differenceInCalendarDays';
import endOfDay from 'date-fns/endOfDay';
import format from 'date-fns/format';
import fromUnixTime from 'date-fns/fromUnixTime';
import getUnixTime from 'date-fns/getUnixTime';
import startOfDay from 'date-fns/startOfDay';
import subDays from 'date-fns/subDays';
import { getQuantileIntervals } from '@chatwoot/utils';
import reportsAPI from 'dashboard/api/reports';
import {
  downloadCsvFile,
  generateFileName,
} from 'dashboard/helper/downloadHelper';
import BarChart from 'shared/components/charts/BarChart.vue';
import {
  buildDatasetHoverDimmingHandler,
  buildIntercomLikeBarTooltip,
  removeTooltipEl,
} from 'shared/components/charts/barInteractions';
import CompactDateInboxFilters from './components/CompactDateInboxFilters.vue';
import { DATE_RANGE_OPTIONS } from './constants';

const METRIC_KEY = 'conversations_count';
const HEATMAP_QUANTILES = [0.2, 0.4, 0.6, 0.8, 0.9, 0.99];
const SUPPORTED_RANGE_IDS = [
  DATE_RANGE_OPTIONS.TODAY.id,
  DATE_RANGE_OPTIONS.YESTERDAY.id,
  DATE_RANGE_OPTIONS.LAST_7_DAYS.id,
  DATE_RANGE_OPTIONS.LAST_30_DAYS.id,
  DATE_RANGE_OPTIONS.LAST_3_MONTHS.id,
  DATE_RANGE_OPTIONS.CUSTOM_DATE_RANGE.id,
];

const { t } = useI18n();

const defaultAvailabilitySummary = () => ({
  current_period: {},
  previous_period: {},
  availability: {
    current_online_seconds: 0,
    previous_online_seconds: 0,
  },
  chats_by_status: {
    current: {
      total_count: 0,
      online_count: 0,
      offline_count: 0,
      online_percentage: 0,
      offline_percentage: 0,
    },
    previous: {
      total_count: 0,
      online_count: 0,
      offline_count: 0,
      online_percentage: 0,
      offline_percentage: 0,
    },
  },
});

const filters = ref({
  from: 0,
  to: 0,
  userIds: [],
  labelId: null,
  inboxId: null,
  teamId: null,
  vipLevelId: null,
  issueId: null,
  priority: null,
});
const isLoading = ref(false);
const isHeatmapLoading = ref(false);
const isAvailabilityLoading = ref(false);
const comparisonError = ref('');
const heatmapError = ref('');
const availabilityError = ref('');
const currentPeriodSeries = ref([]);
const heatmapSeries = ref([]);
const availabilitySummary = ref(defaultAvailabilitySummary());
const generatedAt = ref(null);
const isBreakdownOpen = ref(true);
const heatmapWrapRef = ref(null);
const hoveredHeatmapCell = ref(null);
const latestRefreshToken = ref(0);

const createRefreshToken = () => {
  latestRefreshToken.value += 1;
  return latestRefreshToken.value;
};

const isLatestRefresh = refreshToken =>
  refreshToken === latestRefreshToken.value;

const currentPeriod = computed(() => {
  const defaultFromDate = startOfDay(subDays(new Date(), 6));
  const defaultToDate = endOfDay(new Date());
  const fromDate = filters.value.from
    ? startOfDay(fromUnixTime(filters.value.from))
    : defaultFromDate;
  const toDate = filters.value.to
    ? endOfDay(fromUnixTime(filters.value.to))
    : defaultToDate;

  return {
    from: filters.value.from || getUnixTime(fromDate),
    to: filters.value.to || getUnixTime(toDate),
    fromDate,
    toDate,
    days: Math.max(differenceInCalendarDays(toDate, fromDate) + 1, 1),
  };
});

const reportFilters = computed(() => ({
  userIds: filters.value.userIds,
  labelId: filters.value.labelId,
  teamId: filters.value.teamId,
  vipLevelId: filters.value.vipLevelId,
  issueId: filters.value.issueId,
  priority: filters.value.priority,
}));

const reportScope = computed(() => {
  const inboxId = Number(filters.value.inboxId || 0);
  if (Number.isFinite(inboxId) && inboxId > 0) {
    return { type: 'inbox', id: inboxId };
  }
  return { type: 'account', id: undefined };
});

const normalizeSeries = series => {
  if (!Array.isArray(series)) return [];
  return [...series].sort((left, right) => left.timestamp - right.timestamp);
};

const formatPeriodLabel = period => {
  return `${format(fromUnixTime(period.from), 'dd MMM yy')} - ${format(
    fromUnixTime(period.to),
    'dd MMM yy'
  )}`;
};

const currentPeriodLabel = computed(() =>
  formatPeriodLabel(currentPeriod.value)
);

const availabilityCurrentStatus = computed(
  () => availabilitySummary.value?.chats_by_status?.current || {}
);
const availabilityPreviousStatus = computed(
  () => availabilitySummary.value?.chats_by_status?.previous || {}
);

const availabilityCurrentPeriodLabel = computed(() => {
  const period = availabilitySummary.value?.current_period || {};
  if (!period.from || !period.to) return currentPeriodLabel.value;
  return formatPeriodLabel(period);
});

const availabilityPreviousPeriodLabel = computed(() => {
  const period = availabilitySummary.value?.previous_period || {};
  if (!period.from || !period.to) return 'Previous period';
  return formatPeriodLabel(period);
});

const currentAvailabilitySeconds = computed(() =>
  Number(availabilitySummary.value?.availability?.current_online_seconds || 0)
);
const previousAvailabilitySeconds = computed(() =>
  Number(availabilitySummary.value?.availability?.previous_online_seconds || 0)
);
const availabilitySecondsDiff = computed(
  () => currentAvailabilitySeconds.value - previousAvailabilitySeconds.value
);

const currentOnlineChats = computed(() =>
  Number(availabilityCurrentStatus.value?.online_count || 0)
);
const previousOnlineChats = computed(() =>
  Number(availabilityPreviousStatus.value?.online_count || 0)
);
const onlineChatsDiff = computed(
  () => currentOnlineChats.value - previousOnlineChats.value
);

const currentOfflineChats = computed(() =>
  Number(availabilityCurrentStatus.value?.offline_count || 0)
);
const previousOfflineChats = computed(() =>
  Number(availabilityPreviousStatus.value?.offline_count || 0)
);
const offlineChatsDiff = computed(
  () => currentOfflineChats.value - previousOfflineChats.value
);

const currentAvailabilityTotalChats = computed(() =>
  Number(availabilityCurrentStatus.value?.total_count || 0)
);
const previousAvailabilityTotalChats = computed(() =>
  Number(availabilityPreviousStatus.value?.total_count || 0)
);
const totalChatsDiff = computed(
  () =>
    currentAvailabilityTotalChats.value - previousAvailabilityTotalChats.value
);

const currentOfflinePercentage = computed(() =>
  Number(availabilityCurrentStatus.value?.offline_percentage || 0)
);
const previousOfflinePercentage = computed(() =>
  Number(availabilityPreviousStatus.value?.offline_percentage || 0)
);
const offlinePercentageDiff = computed(
  () => currentOfflinePercentage.value - previousOfflinePercentage.value
);

const formatDuration = totalSeconds => {
  const seconds = Number(totalSeconds || 0);
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  return `${hours}h ${minutes}m`;
};

const formatDurationDiff = totalSeconds => {
  const numericValue = Number(totalSeconds || 0);
  const sign = numericValue > 0 ? '+' : '';
  return `${sign}${formatDuration(Math.abs(numericValue))}`;
};

const formatSignedCount = value => {
  const numericValue = Number(value || 0);
  const sign = numericValue > 0 ? '+' : '';
  return `${sign}${numericValue.toLocaleString()}`;
};

const formatPercent = value => `${Number(value || 0).toFixed(2)}%`;

const formatPercentDiff = value => {
  const numericValue = Number(value || 0);
  const sign = numericValue > 0 ? '+' : '';
  return `${sign}${numericValue.toFixed(2)}%`;
};

const diffClass = value => {
  if (value > 0) return 'text-n-teal-10';
  if (value < 0) return 'text-n-ruby-9';
  return 'text-n-slate-11';
};

const dayRows = computed(() => {
  const seriesMap = currentPeriodSeries.value.reduce((acc, item) => {
    const dayKey = format(fromUnixTime(item.timestamp), 'yyyy-MM-dd');
    acc[dayKey] = Number(item.value || 0);
    return acc;
  }, {});

  return Array.from({ length: currentPeriod.value.days }, (_, index) => {
    const date = addDays(currentPeriod.value.fromDate, index);
    const ts = getUnixTime(date);
    const dayKey = format(date, 'yyyy-MM-dd');
    return {
      timestamp: ts,
      dayKey,
      value: Number(seriesMap[dayKey] || 0),
      shortLabel: format(date, 'dd MMM'),
      tooltipLabel: format(date, "dd MMM''yy"),
      breakdownLabel: format(date, 'dd MMM yy'),
    };
  });
});

const currentTotalChats = computed(() =>
  dayRows.value.reduce((sum, row) => sum + Number(row.value || 0), 0)
);

const hasConversationData = computed(() =>
  dayRows.value.some(row => Number(row.value || 0) > 0)
);

const chartCollection = computed(() => ({
  labels: dayRows.value.map(row => row.shortLabel),
  datasets: [
    {
      label: t('TOTAL_CHATS_REPORT.TOTAL_CHATS'),
      data: dayRows.value.map(row => row.value),
      backgroundColor: '#1D75E5',
      borderRadius: 4,
      categoryPercentage: 0.7,
      barPercentage: 0.9,
    },
  ],
}));

const tooltipId = 'total-chats-tooltip';

const chartOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  animation: { duration: 0 },
  interaction: { mode: 'nearest', intersect: true },
  onHover: buildDatasetHoverDimmingHandler(),
  plugins: {
    legend: {
      display: false,
    },
    tooltip: {
      enabled: false,
      displayColors: false,
      external: buildIntercomLikeBarTooltip({
        tooltipId,
        titleForIndex: index => dayRows.value[index]?.tooltipLabel || '',
        seriesLabelForDataset: () => t('TOTAL_CHATS_REPORT.TOTAL_CHATS'),
        showPercent: false,
      }),
    },
  },
  scales: {
    y: {
      beginAtZero: true,
      ticks: {
        callback: value => Number(value).toLocaleString(),
      },
    },
  },
}));

const dayColumns = computed(() => {
  return Array.from({ length: currentPeriod.value.days }, (_, index) => {
    const date = addDays(currentPeriod.value.fromDate, index);
    return {
      key: format(date, 'yyyy-MM-dd'),
      label: format(date, 'dd MMM'),
      tooltipLabel: format(date, 'dd MMM yy'),
    };
  });
});

const hourlyRows = computed(() => {
  return Array.from({ length: 24 }, (_, hour) => hour);
});

const heatmapValueMap = computed(() => {
  return heatmapSeries.value.reduce((acc, item) => {
    const date = fromUnixTime(item.timestamp);
    const dayKey = format(date, 'yyyy-MM-dd');
    const key = `${dayKey}-${date.getHours()}`;
    acc[key] = Number(item.value || 0);
    return acc;
  }, {});
});

const heatmapRows = computed(() => {
  return hourlyRows.value.map(hour => {
    return {
      hour,
      cells: dayColumns.value.map(day => {
        const key = `${day.key}-${hour}`;
        const value = heatmapValueMap.value[key] || 0;
        return {
          key,
          value,
          dayLabel: day.tooltipLabel,
        };
      }),
    };
  });
});

const heatmapValues = computed(() =>
  Object.values(heatmapValueMap.value).map(value => Number(value || 0))
);

const hasHeatmapData = computed(() =>
  heatmapValues.value.some(value => Number(value || 0) > 0)
);

const heatmapQuantiles = computed(() =>
  getQuantileIntervals(heatmapValues.value, HEATMAP_QUANTILES)
);

const heatmapLegendTicks = computed(() => {
  const values = heatmapValues.value;
  const max = Math.max(0, ...values);
  if (max <= 0) return [];
  const ticks = [
    1,
    Math.round(max * 0.25),
    Math.round(max * 0.5),
    Math.round(max * 0.75),
    max,
  ].filter(v => v > 0);
  return [...new Set(ticks)].sort((a, b) => a - b);
});

const getHeatmapCellClass = value => {
  if (!value) return 'bg-n-slate-2';

  let level = [...heatmapQuantiles.value, Infinity].findIndex(
    range => value <= range && value > 0
  );

  if (level > 6) level = 5;
  if (level === 0) return 'bg-n-slate-2';

  const classes = [
    'bg-n-blue-3',
    'bg-n-blue-5',
    'bg-n-blue-7',
    'bg-n-blue-8',
    'bg-n-blue-10',
    'bg-n-blue-11',
  ];

  return classes[level - 1];
};

const formatHourLabel = hour => {
  const hour12 = hour % 12 || 12;
  const suffix = hour < 12 ? 'am' : 'pm';
  return `${hour12}:00 ${suffix}`;
};

const onHeatmapCellEnter = (event, cell, hour) => {
  const wrapEl = heatmapWrapRef.value;
  const target = event?.currentTarget;
  if (!wrapEl || !target) return;
  const wrapRect = wrapEl.getBoundingClientRect();
  const rect = target.getBoundingClientRect();
  hoveredHeatmapCell.value = {
    left: rect.left - wrapRect.left + rect.width / 2,
    top: rect.top - wrapRect.top,
    hour,
    dayLabel: cell.dayLabel,
    value: Number(cell.value || 0),
  };
};

const onHeatmapCellLeave = () => {
  hoveredHeatmapCell.value = null;
};

const generatedAtLabel = computed(() => {
  if (!generatedAt.value) {
    return t('LAST_7_DAYS_REPORT.NOT_GENERATED_YET');
  }

  return generatedAt.value.toLocaleString();
});

const toCsvValue = value => {
  const stringValue = String(value ?? '');
  if (/[",\n]/.test(stringValue)) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }
  return stringValue;
};

const rowsToCsv = (headers, rows) => {
  const csvRows = [headers, ...rows];
  return csvRows
    .map(row => row.map(column => toCsvValue(column)).join(','))
    .join('\n');
};

const downloadCurrentViewCsv = () => {
  const headers = [
    t('TOTAL_CHATS_REPORT.CURRENT_DATE'),
    t('TOTAL_CHATS_REPORT.CURRENT_VALUE'),
  ];

  const rows = dayRows.value.map(row => [row.breakdownLabel, row.value]);

  const csvContent = rowsToCsv(headers, rows);
  const fileName = generateFileName({
    type: 'total-chats-breakdown',
    to: currentPeriod.value.to,
  });
  downloadCsvFile(fileName, csvContent);
};

const downloadHeatmapCsv = () => {
  const headers = [
    t('TOTAL_CHATS_REPORT.HOUR'),
    ...dayColumns.value.map(day => day.label),
  ];
  const rows = heatmapRows.value.map(row => [
    formatHourLabel(row.hour),
    ...row.cells.map(cell => cell.value),
  ]);
  const csvContent = rowsToCsv(headers, rows);
  const fileName = generateFileName({
    type: 'total-chats-heatmap',
    to: currentPeriod.value.to,
  });
  downloadCsvFile(fileName, csvContent);
};

const fetchComparisonData = async refreshToken => {
  if (isLatestRefresh(refreshToken)) {
    isLoading.value = true;
    comparisonError.value = '';
  }

  try {
    const response = await reportsAPI.getReports({
      metric: METRIC_KEY,
      from: currentPeriod.value.from,
      to: currentPeriod.value.to,
      type: reportScope.value.type,
      id: reportScope.value.id,
      groupBy: 'day',
      businessHours: false,
      ...reportFilters.value,
    });

    if (!isLatestRefresh(refreshToken)) return;

    currentPeriodSeries.value = normalizeSeries(response.data);
    generatedAt.value = new Date();
  } catch {
    if (!isLatestRefresh(refreshToken)) return;

    comparisonError.value = t('TOTAL_CHATS_REPORT.FETCH_ERROR');
    currentPeriodSeries.value = [];
  } finally {
    if (isLatestRefresh(refreshToken)) {
      isLoading.value = false;
    }
  }
};

const fetchHeatmapData = async refreshToken => {
  if (isLatestRefresh(refreshToken)) {
    isHeatmapLoading.value = true;
    heatmapError.value = '';
  }

  try {
    const response = await reportsAPI.getReports({
      metric: METRIC_KEY,
      from: currentPeriod.value.from,
      to: currentPeriod.value.to,
      type: reportScope.value.type,
      id: reportScope.value.id,
      groupBy: 'hour',
      businessHours: false,
      ...reportFilters.value,
    });

    if (!isLatestRefresh(refreshToken)) return;

    heatmapSeries.value = normalizeSeries(response.data);
    generatedAt.value = new Date();
  } catch {
    if (!isLatestRefresh(refreshToken)) return;

    heatmapError.value = t('TOTAL_CHATS_REPORT.FETCH_ERROR');
    heatmapSeries.value = [];
  } finally {
    if (isLatestRefresh(refreshToken)) {
      isHeatmapLoading.value = false;
    }
  }
};

const fetchAvailabilityInsights = async refreshToken => {
  if (isLatestRefresh(refreshToken)) {
    isAvailabilityLoading.value = true;
    availabilityError.value = '';
  }

  try {
    const response = await reportsAPI.getChatAvailability({
      from: currentPeriod.value.from,
      to: currentPeriod.value.to,
      inboxId: filters.value.inboxId,
      livechatOnly: true,
      ...reportFilters.value,
    });

    if (!isLatestRefresh(refreshToken)) return;

    availabilitySummary.value =
      response?.data?.summary || defaultAvailabilitySummary();
    generatedAt.value = new Date();
  } catch {
    if (!isLatestRefresh(refreshToken)) return;

    availabilityError.value = t('CHAT_AVAILABILITY_REPORT.FETCH_ERROR');
    availabilitySummary.value = defaultAvailabilitySummary();
  } finally {
    if (isLatestRefresh(refreshToken)) {
      isAvailabilityLoading.value = false;
    }
  }
};

const refreshData = async () => {
  const refreshToken = createRefreshToken();
  await Promise.all([
    fetchComparisonData(refreshToken),
    fetchHeatmapData(refreshToken),
    fetchAvailabilityInsights(refreshToken),
  ]);
};

const onFilterChange = ({
  from,
  to,
  selectedAgents = [],
  selectedLabel = null,
  selectedInbox = null,
  selectedTeam = null,
  selectedVip = null,
  selectedIssue = null,
  selectedPriority = null,
}) => {
  filters.value = {
    from,
    to,
    userIds: Array.isArray(selectedAgents)
      ? selectedAgents.map(agent => agent.id)
      : [],
    labelId: selectedLabel?.id || null,
    inboxId: selectedInbox?.id || null,
    teamId: selectedTeam?.id || null,
    vipLevelId: selectedVip?.id || null,
    issueId: selectedIssue?.id || null,
    priority: selectedPriority?.id || null,
  };
  refreshData();
};

onBeforeUnmount(() => {
  removeTooltipEl(tooltipId);
});
</script>

<template>
  <div class="tc-page">
    <div class="tc-shell">
      <div class="tc-header">
        <div class="tc-header-left">
          <h1 class="tc-title">{{ t('TOTAL_CHATS_REPORT.TITLE') }}</h1>
        </div>
      </div>

      <div class="tc-toolbar">
        <div class="tc-toolbar-left">
          <CompactDateInboxFilters
            :allowed-date-range-ids="SUPPORTED_RANGE_IDS"
            show-agents
            show-labels
            show-teams
            show-vip
            show-issue
            show-priority
            hide-inactive-filters
            @filter-change="onFilterChange"
          />
        </div>
      </div>

      <div class="tc-content">
        <div v-if="comparisonError" class="tc-error">
          {{ comparisonError }}
        </div>

        <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
        <section class="tc-card">
          <div class="tc-card-header">
            <div class="tc-card-title-wrap">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                width="20"
                height="20"
              >
                <path
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 8h.01M11 12h1v4h1M3 12a9 9 0 1 0 18.001 0A9 9 0 0 0 3 12"
                />
              </svg>
              <h2 class="tc-card-title">
                {{ t('TOTAL_CHATS_REPORT.CARD_TITLE') }}
              </h2>
              <span class="tc-period">{{ currentPeriodLabel }}</span>
            </div>

            <button
              class="tc-export-btn"
              type="button"
              @click="downloadCurrentViewCsv"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                width="16"
                height="16"
              >
                <path
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2m-8-1V4m0 12 5-5m-5 5-5-5"
                />
              </svg>
              <span>{{ t('TOTAL_CHATS_REPORT.EXPORT_CSV') }}</span>
            </button>
          </div>

          <div class="tc-section tc-section--no-bottom">
            <div class="tc-big-number">
              {{ currentTotalChats.toLocaleString() }}
            </div>

            <div class="tc-chart">
              <woot-loading-state
                v-if="isLoading"
                :message="$t('REPORT.LOADING_CHART')"
                class="h-full"
              />
              <div v-else-if="!hasConversationData" class="tc-empty">
                {{ t('TOTAL_CHATS_REPORT.NO_DATA') }}
              </div>
              <BarChart
                v-else
                :collection="chartCollection"
                :chart-options="chartOptions"
              />
            </div>
          </div>

          <div class="tc-breakdown">
            <div
              class="tc-breakdown-header"
              role="button"
              tabindex="0"
              @click="isBreakdownOpen = !isBreakdownOpen"
              @keydown.enter.prevent="isBreakdownOpen = !isBreakdownOpen"
              @keydown.space.prevent="isBreakdownOpen = !isBreakdownOpen"
            >
              <div class="tc-breakdown-title">
                <svg
                  class="tc-breakdown-caret"
                  :class="{ 'is-collapsed': !isBreakdownOpen }"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  width="20"
                  height="20"
                >
                  <path
                    stroke="currentColor"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="m6 9 6 6 6-6"
                  />
                </svg>
                <span>{{ t('TOTAL_CHATS_REPORT.BREAKDOWN_TITLE') }}</span>
              </div>
            </div>

            <div v-show="isBreakdownOpen" class="tc-table-wrap">
              <table class="tc-table">
                <thead>
                  <tr>
                    <th class="tc-th-series">
                      {{ t('TOTAL_CHATS_REPORT.SERIES') }}
                    </th>
                    <th
                      v-for="row in dayRows"
                      :key="`header-${row.timestamp}`"
                      class="tc-th-data"
                    >
                      <div>{{ row.breakdownLabel }}</div>
                    </th>
                    <th class="tc-th-data">
                      <div>Total</div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td class="tc-td-series">
                      {{ t('TOTAL_CHATS_REPORT.TOTAL_CHATS') }}
                    </td>
                    <td
                      v-for="row in dayRows"
                      :key="`row-${row.timestamp}`"
                      class="tc-td-data"
                    >
                      <div class="tc-cell-main">
                        {{ row.value.toLocaleString() }}
                      </div>
                    </td>
                    <td class="tc-td-data">
                      <div class="tc-cell-main">
                        {{ currentTotalChats.toLocaleString() }}
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>

        <section class="tc-card">
          <div class="tc-card-header">
            <div class="tc-card-title-wrap">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                width="20"
                height="20"
              >
                <path
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 8h.01M11 12h1v4h1M3 12a9 9 0 1 0 18.001 0A9 9 0 0 0 3 12"
                />
              </svg>
              <h2 class="tc-card-title">Availability insights</h2>
              <span class="tc-period">{{
                availabilityCurrentPeriodLabel
              }}</span>
            </div>
          </div>

          <div class="tc-section">
            <p class="tc-card-note">
              Live chat only. Metrics are calculated in your selected date range
              and local timezone.
            </p>

            <woot-loading-state
              v-if="isAvailabilityLoading"
              :message="$t('REPORT.LOADING_CHART')"
            />

            <template v-else>
              <div v-if="availabilityError" class="tc-error">
                {{ availabilityError }}
              </div>

              <div class="tc-metric-row">
                <article class="tc-metric">
                  <p class="tc-metric-label">Total live chats</p>
                  <p class="tc-metric-sub">
                    {{ availabilityCurrentPeriodLabel }}
                  </p>
                  <div class="tc-metric-value-row">
                    <span class="tc-metric-value">
                      {{ currentAvailabilityTotalChats.toLocaleString() }}
                    </span>
                  </div>
                  <p class="tc-metric-sub">
                    Previous ({{ availabilityPreviousPeriodLabel }}):
                    {{ previousAvailabilityTotalChats.toLocaleString() }}
                    <span
                      class="tc-metric-change"
                      :class="diffClass(totalChatsDiff)"
                    >
                      ({{ formatSignedCount(totalChatsDiff) }})
                    </span>
                  </p>
                </article>

                <article class="tc-metric">
                  <p class="tc-metric-label">Online chats</p>
                  <p class="tc-metric-sub">
                    {{ availabilityCurrentPeriodLabel }}
                  </p>
                  <div class="tc-metric-value-row">
                    <span class="tc-metric-value">
                      {{ currentOnlineChats.toLocaleString() }}
                    </span>
                  </div>
                  <p class="tc-metric-sub">
                    Previous ({{ availabilityPreviousPeriodLabel }}):
                    {{ previousOnlineChats.toLocaleString() }}
                    <span
                      class="tc-metric-change"
                      :class="diffClass(onlineChatsDiff)"
                    >
                      ({{ formatSignedCount(onlineChatsDiff) }})
                    </span>
                  </p>
                </article>

                <article class="tc-metric">
                  <p class="tc-metric-label">Offline chats</p>
                  <p class="tc-metric-sub">
                    {{ availabilityCurrentPeriodLabel }}
                  </p>
                  <div class="tc-metric-value-row">
                    <span class="tc-metric-value">
                      {{ currentOfflineChats.toLocaleString() }}
                    </span>
                  </div>
                  <p class="tc-metric-sub">
                    Previous ({{ availabilityPreviousPeriodLabel }}):
                    {{ previousOfflineChats.toLocaleString() }}
                    <span
                      class="tc-metric-change"
                      :class="diffClass(offlineChatsDiff)"
                    >
                      ({{ formatSignedCount(offlineChatsDiff) }})
                    </span>
                  </p>
                </article>

                <article class="tc-metric">
                  <p class="tc-metric-label">Offline rate</p>
                  <p class="tc-metric-sub">
                    {{ availabilityCurrentPeriodLabel }}
                  </p>
                  <div class="tc-metric-value-row">
                    <span class="tc-metric-value">
                      {{ formatPercent(currentOfflinePercentage) }}
                    </span>
                  </div>
                  <p class="tc-metric-sub">
                    Previous ({{ availabilityPreviousPeriodLabel }}):
                    {{ formatPercent(previousOfflinePercentage) }}
                    <span
                      class="tc-metric-change"
                      :class="diffClass(offlinePercentageDiff)"
                    >
                      ({{ formatPercentDiff(offlinePercentageDiff) }})
                    </span>
                  </p>
                </article>

                <article class="tc-metric">
                  <p class="tc-metric-label">Agent online time</p>
                  <p class="tc-metric-sub">
                    {{ availabilityCurrentPeriodLabel }}
                  </p>
                  <div class="tc-metric-value-row">
                    <span class="tc-metric-value">
                      {{ formatDuration(currentAvailabilitySeconds) }}
                    </span>
                  </div>
                  <p class="tc-metric-sub">
                    Previous ({{ availabilityPreviousPeriodLabel }}):
                    {{ formatDuration(previousAvailabilitySeconds) }}
                    <span
                      class="tc-metric-change"
                      :class="diffClass(availabilitySecondsDiff)"
                    >
                      ({{ formatDurationDiff(availabilitySecondsDiff) }})
                    </span>
                  </p>
                </article>
              </div>
            </template>
          </div>
        </section>
        <!-- eslint-enable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->

        <section data-testid="heatmap-chart" class="tc-card tc-card--heatmap">
          <div class="tc-card-header">
            <div class="tc-card-title-wrap">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                width="20"
                height="20"
              >
                <path
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 8h.01M11 12h1v4h1M3 12a9 9 0 1 0 18.001 0A9 9 0 0 0 3 12"
                />
              </svg>
              <h2 class="tc-card-title">
                {{ t('TOTAL_CHATS_REPORT.HEATMAP_TITLE') }}
              </h2>
              <span class="tc-period">{{ currentPeriodLabel }}</span>
            </div>

            <button
              class="tc-export-btn"
              type="button"
              @click="downloadHeatmapCsv"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                width="16"
                height="16"
              >
                <path
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2m-8-1V4m0 12 5-5m-5 5-5-5"
                />
              </svg>
              <span>{{ t('TOTAL_CHATS_REPORT.EXPORT_CSV') }}</span>
            </button>
          </div>

          <div class="tc-section">
            <div ref="heatmapWrapRef" class="tc-heatmap-wrap">
              <woot-loading-state
                v-if="isHeatmapLoading"
                :message="$t('REPORT.LOADING_CHART')"
              />

              <div v-else-if="heatmapError" class="tc-empty tc-empty--heatmap">
                {{ heatmapError }}
              </div>

              <div
                v-else-if="heatmapRows.length && hasHeatmapData"
                class="tc-heatmap-table-wrap"
              >
                <div
                  v-if="hoveredHeatmapCell"
                  class="tc-heatmap-tooltip"
                  :style="{
                    left: `${hoveredHeatmapCell.left}px`,
                    top: `${hoveredHeatmapCell.top}px`,
                  }"
                >
                  <div class="tc-heatmap-tooltip__time">
                    {{ hoveredHeatmapCell.dayLabel }}
                    {{ formatHourLabel(hoveredHeatmapCell.hour) }}
                  </div>
                  <div class="tc-heatmap-tooltip__line">
                    <span class="tc-heatmap-tooltip__dot" />
                    <span>
                      {{
                        [
                          t('TOTAL_CHATS_REPORT.TOTAL_CHATS'),
                          hoveredHeatmapCell.value.toLocaleString(),
                        ].join(': ')
                      }}
                    </span>
                  </div>
                </div>

                <table class="tc-heatmap-table">
                  <thead>
                    <tr>
                      <th class="tc-heatmap-th tc-heatmap-th--hour">
                        {{ t('TOTAL_CHATS_REPORT.HOUR') }}
                      </th>
                      <th
                        v-for="day in dayColumns"
                        :key="day.key"
                        class="tc-heatmap-th"
                      >
                        {{ day.label }}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="row in heatmapRows" :key="`hour-${row.hour}`">
                      <td class="tc-heatmap-td tc-heatmap-td--hour">
                        {{
                          row.hour % 3 === 0 ? formatHourLabel(row.hour) : ''
                        }}
                      </td>
                      <td
                        v-for="cell in row.cells"
                        :key="cell.key"
                        class="tc-heatmap-td"
                        @mouseenter="onHeatmapCellEnter($event, cell, row.hour)"
                        @mouseleave="onHeatmapCellLeave"
                      >
                        <div
                          class="tc-heatmap-cell"
                          :class="getHeatmapCellClass(cell.value)"
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>

                <div v-if="heatmapLegendTicks.length" class="tc-heatmap-legend">
                  <div class="tc-heatmap-legend__bar" />
                  <div class="tc-heatmap-legend__ticks">
                    <span
                      v-for="tick in heatmapLegendTicks"
                      :key="`tick-${tick}`"
                    >
                      {{ tick.toLocaleString() }}
                    </span>
                  </div>
                </div>
              </div>

              <div v-else class="tc-empty tc-empty--heatmap">
                {{ t('TOTAL_CHATS_REPORT.NO_DATA') }}
              </div>
            </div>
          </div>
        </section>

        <p class="tc-footer-note">
          {{ t('TOTAL_CHATS_REPORT.GENERATED_AT', { time: generatedAtLabel }) }}
        </p>
      </div>
    </div>
  </div>
</template>

<style scoped>
.tc-page {
  min-height: 100vh;
  min-height: 100dvh;
  width: 100%;
  box-sizing: border-box;
  background: #ffffff;
  color: #111827;
}
.tc-shell {
  min-width: 650px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #ffffff;
  overflow: visible;
}

.tc-header {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  min-height: 58px;
  padding: 0 24px;
  border-bottom: 1px solid #eceff3;
  position: sticky;
  top: 0;
  z-index: 50;
  background: #ffffff;
}
.tc-header-left {
  display: flex;
  align-items: center;
  gap: 0;
}
.tc-title {
  margin: 0;
  font-size: 18px !important;
  line-height: 24px;
  font-weight: 600;
  letter-spacing: -0.02em;
  color: #111827;
}

.tc-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 24px 8px;
  background: #ffffff;
  box-shadow: rgba(0, 0, 0, 0.3) 0px 3px 10px -6px;
  position: sticky;
  top: 58px;
  z-index: 49;
}
.tc-toolbar-left {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.tc-filter-anchor {
  position: relative;
}
.tc-secondary-btn {
  height: 34px;
  border-radius: 8px;
  border: 1px solid #d9dde3;
  background: #ffffff;
  color: #111827;
  font-size: 12px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 0 12px;
  cursor: pointer;
}
.tc-secondary-btn:hover {
  background: #f9fafb;
}
.tc-menu {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  min-width: 220px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #ffffff;
  box-shadow: rgba(0, 0, 0, 0.12) 0px 14px 38px;
  padding: 6px;
  z-index: 30;
}
.tc-menu-item {
  width: 100%;
  text-align: left;
  padding: 8px 10px;
  border-radius: 8px;
  background: transparent;
  border: none;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  color: #111827;
}
.tc-menu-item:hover {
  background: #f3f4f6;
}
.tc-menu-item.is-selected {
  background: #eef2ff;
  color: #1d4ed8;
}

.tc-menu-custom {
  margin-top: 8px;
  border-top: 1px solid #eef0f3;
  padding-top: 8px;
}

.tc-date-picker {
  width: 100%;
}

.tc-content {
  padding: 16px 32px;
}
.tc-error {
  margin-bottom: 12px;
  border: 1px solid #fecaca;
  background: #fef2f2;
  color: #b91c1c;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 13px;
}

.tc-card {
  border-radius: 4px;
  border: 1px solid #d8dee5;
  background: #fff;
  box-sizing: border-box;
  margin-bottom: 20px;
  overflow: hidden;
}
.tc-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 18px 20px;
  border-bottom: 1px solid #e5e7eb;
}
.tc-card-title-wrap {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}
.tc-card-title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #111827;
}
.tc-period {
  font-size: 12px;
  color: #6b7280;
  margin-left: 10px;
  white-space: nowrap;
}

.tc-export-btn {
  min-width: 128px;
  height: 36px;
  border-radius: 8px;
  border: 1px solid #d1d5db;
  background: #ffffff;
  color: #111827;
  padding: 0 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
}
.tc-export-btn:hover {
  background: #f9fafb;
}

.tc-section {
  padding: 16px 20px 20px;
}
.tc-section--no-bottom {
  padding-bottom: 0;
}

.tc-big-number {
  font-size: 32px;
  line-height: 38px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 8px;
}

.tc-card-note {
  margin: 0 0 14px;
  font-size: 12px;
  color: #6b7280;
}

.tc-metric-row {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
}
@media (max-width: 1100px) {
  .tc-metric-row {
    grid-template-columns: 1fr;
  }
}
.tc-metric {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #f8fafc;
  padding: 12px;
}
.tc-metric-label {
  font-size: 13px;
  font-weight: 600;
  color: #111827;
}
.tc-metric-sub {
  margin-top: 2px;
  font-size: 12px;
  color: #6b7280;
}

.tc-metric-change {
  font-weight: 700;
}
.tc-metric-value-row {
  margin-top: 10px;
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}
.tc-metric-value {
  font-size: 28px;
  line-height: 32px;
  font-weight: 700;
  color: #111827;
}
.tc-metric-badge {
  border-radius: 6px;
  background: #e5e7eb;
  color: #374151;
  font-size: 12px;
  font-weight: 800;
  padding: 4px 6px;
}

.tc-chart {
  height: 384px;
  margin-top: 16px;
}
.tc-empty {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  font-size: 13px;
}
.tc-empty--heatmap {
  height: 160px;
}

.tc-breakdown {
  border-top: 1px solid #dfe3e8;
}
.tc-breakdown-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 15px 16px;
  cursor: pointer;
  user-select: none;
}
.tc-breakdown-title {
  color: #111827;
  font-size: 14px;
  font-weight: 600;
  line-height: 24px;
  display: flex;
  align-items: center;
  gap: 10px;
}
.tc-breakdown-caret {
  transition: transform 0.18s ease;
}
.tc-breakdown-caret.is-collapsed {
  transform: rotate(-90deg);
}

.tc-table-wrap {
  overflow-x: auto;
  border-top: 1px solid #dfe3e8;
}
.tc-table {
  width: max-content;
  min-width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 13px;
  color: #424d57;
}
.tc-th-series {
  position: sticky;
  left: 0;
  z-index: 2;
  background: #ffffff;
  border-right: 1px solid #edf1f5;
  padding: 10px 12px;
  text-align: left;
  font-weight: 600;
  color: #111827;
}
.tc-th-data {
  padding: 10px 12px;
  text-align: left;
  font-weight: 700;
  color: #111827;
  border-bottom: 1px solid #edf1f5;
  background: #f8fafc;
}
.tc-th-sub {
  margin-top: 2px;
  font-size: 11px;
  color: #6b7280;
  font-weight: 600;
}
.tc-td-series {
  position: sticky;
  left: 0;
  z-index: 1;
  background: #ffffff;
  border-right: 1px solid #edf1f5;
  padding: 12px;
  font-weight: 600;
  color: #111827;
  border-bottom: 1px solid #edf1f5;
}
.tc-td-data {
  padding: 10px 12px;
  border-bottom: 1px solid #edf1f5;
  vertical-align: top;
}
.tc-cell-main {
  font-weight: 700;
  color: #111827;
}
.tc-cell-sub {
  margin-top: 2px;
  font-size: 11px;
  color: #6b7280;
  font-weight: 600;
}
.tc-cell-diff {
  margin-top: 2px;
  font-size: 11px;
  font-weight: 700;
}

.tc-heatmap-sub {
  margin: 0 0 12px;
  font-size: 13px;
  color: #6b7280;
}
.tc-heatmap-wrap {
  overflow-x: auto;
  position: relative;
}
.tc-heatmap-table-wrap {
  position: relative;
  overflow-x: auto;
  padding-bottom: 8px;
}
.tc-heatmap-table {
  width: max-content;
  min-width: 100%;
  border-collapse: separate;
  border-spacing: 4px;
}
.tc-heatmap-th {
  padding: 4px 6px;
  text-align: center;
  font-size: 12px;
  font-weight: 700;
  color: #111827;
}
.tc-heatmap-th--hour {
  text-align: left;
  color: #6b7280;
}
.tc-heatmap-td {
  padding: 2px;
}
.tc-heatmap-td--hour {
  font-size: 12px;
  color: #6b7280;
  padding-right: 10px;
  white-space: nowrap;
}
.tc-heatmap-cell {
  height: 13px;
  width: 100%;
  border-radius: 2px;
}

.tc-heatmap-tooltip {
  position: absolute;
  transform: translate(-50%, calc(-100% - 10px));
  background: #ffffff;
  border: 1px solid #d1d5db;
  border-radius: 10px;
  padding: 10px 12px;
  box-shadow: rgba(0, 0, 0, 0.18) 0px 14px 38px;
  pointer-events: none;
  min-width: 180px;
  z-index: 5;
}
.tc-heatmap-tooltip__time {
  font-size: 12px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 6px;
}
.tc-heatmap-tooltip__line {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #374151;
  font-weight: 600;
}
.tc-heatmap-tooltip__dot {
  width: 10px;
  height: 10px;
  border-radius: 3px;
  background: #cbd5e1;
  display: inline-block;
}

.tc-heatmap-legend {
  margin-top: 12px;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
}
.tc-heatmap-legend__bar {
  width: 252px;
  height: 12px;
  border-radius: 6px;
  background: linear-gradient(90deg, #d1e2fa 0%, #4384f5 100%);
  border: 1px solid #e5e7eb;
}
.tc-heatmap-legend__ticks {
  width: 252px;
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: #6b7280;
}

.tc-footer-note {
  margin-top: 8px;
  font-size: 12px;
  color: #6b7280;
  line-height: 1.5;
}

::global(body.dark) .tc-page,
::global(body.dark) .tc-shell,
::global(body.dark) .tc-card,
::global(body.dark) .tc-header,
::global(body.dark) .tc-toolbar,
::global(body.dark) .tc-th-series,
::global(body.dark) .tc-th-data,
::global(body.dark) .tc-td-series,
::global(body.dark) .tc-td-data,
::global(body.dark) .tc-heatmap-th,
::global(body.dark) .tc-heatmap-td,
::global(body.theme-mix) .tc-page,
::global(body.theme-mix) .tc-shell,
::global(body.theme-mix) .tc-card,
::global(body.theme-mix) .tc-header,
::global(body.theme-mix) .tc-toolbar,
::global(body.theme-mix) .tc-th-series,
::global(body.theme-mix) .tc-th-data,
::global(body.theme-mix) .tc-td-series,
::global(body.theme-mix) .tc-td-data,
::global(body.theme-mix) .tc-heatmap-th,
::global(body.theme-mix) .tc-heatmap-td {
  background: #fff !important;
  color: #111827 !important;
}

::global(body.dark) .tc-th-series *,
::global(body.dark) .tc-th-data *,
::global(body.dark) .tc-heatmap-th *,
::global(body.theme-mix) .tc-th-series *,
::global(body.theme-mix) .tc-th-data *,
::global(body.theme-mix) .tc-heatmap-th * {
  color: #111827 !important;
}
</style>
